/**
 * Margin editor — adjust a proposal's commercial margin with a live alert
 * when it drops below the minimum allowed (como o Excel). Shared by solar
 * + BC details.
 *
 * Margin is stored as a multiplier (1.55 = +55%) but shown to the user as
 * a percentage (55%) which is more natural. The alert is a warning, not a
 * block — the comercial can still save a low margin (commercial decision),
 * it just flags it red.
 *
 * Persists via `actions/update_margin` which recomputes the totals server-
 * side from the stored cost.
 */

import { useState } from "react"
import { AlertTriangle, Check, Loader2 } from "lucide-react"
import type { AppShell } from "@/App"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"

export interface MarginEditorProps {
  shell: AppShell | null
  proposalId: string
  /** Current applied margin as a multiplier (e.g. 1.55). */
  appliedMargin: number
  /** Minimum allowed margin multiplier (e.g. 1.25). Below → red alert. */
  minMargin: number
  /** VAT multiplier passed to the recompute (module-specific; default 1.23). */
  vatMultiplier?: number
  /** Called with the new totals after a successful save. */
  onSaved?: (r: { applied_margin: number; total_price_no_vat_eur: number; total_price_eur: number }) => void
}

/** multiplier 1.55 ↔ percent 55 */
const toPct = (mult: number) => Math.round((mult - 1) * 1000) / 10
const fromPct = (pct: number) => 1 + pct / 100

export function MarginEditor({
  shell, proposalId, appliedMargin, minMargin, vatMultiplier = 1.23, onSaved,
}: MarginEditorProps) {
  const [pct, setPct] = useState<string>(String(toPct(appliedMargin)))
  const [saving, setSaving] = useState(false)

  const pctNum = Number(pct)
  const marginMult = fromPct(pctNum)
  const minPct = toPct(minMargin)
  const belowMin = Number.isFinite(pctNum) && marginMult < minMargin
  const dirty = Number.isFinite(pctNum) && Math.abs(marginMult - appliedMargin) > 0.0001
  const valid = Number.isFinite(pctNum) && pctNum >= 0

  async function save() {
    if (!shell || !valid || !dirty) return
    setSaving(true)
    try {
      const res = await shell.client.post<{
        success: boolean
        data?: { applied_margin: number; total_price_no_vat_eur: number; total_price_eur: number }
        error?: string
      }>("actions/update_margin", {
        params: { proposal_id: proposalId, applied_margin: marginMult, vat_multiplier: vatMultiplier },
      })
      if (!res.success || !res.data) throw new Error(res.error || "Falha")
      shell.bridge.toast({ variant: "success", description: "Margem actualizada" })
      onSaved?.(res.data)
    } catch (err) {
      shell.bridge.toast({
        variant: "error",
        description: err instanceof Error ? err.message : "Falha a guardar margem",
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-2">
      <div className="flex items-end gap-2">
        <div className="space-y-1">
          <Label>Margem comercial (%)</Label>
          <div className="flex items-center gap-2">
            <Input
              type="number" step="1" min="0"
              className={`w-28 ${belowMin ? "border-red-400 focus-visible:ring-red-400" : ""}`}
              value={pct}
              onChange={(e) => setPct(e.target.value)}
            />
            <span className="text-sm text-muted-foreground">
              ×{Number.isFinite(marginMult) ? marginMult.toFixed(2) : "—"}
            </span>
          </div>
        </div>
        <Button onClick={save} disabled={!dirty || !valid || saving} size="sm">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
          Aplicar
        </Button>
      </div>
      {belowMin ? (
        <div className="flex items-center gap-1.5 text-xs font-medium text-red-600 dark:text-red-400">
          <AlertTriangle className="h-3.5 w-3.5" />
          Abaixo da margem mínima permitida ({minPct}%)
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">
          Margem mínima recomendada: {minPct}%
        </p>
      )}
    </div>
  )
}
